import React, { useState } from "react";
import { CalendarBlank, Clock, CheckCircle, ArrowRight, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { submitWalkthrough } from "@/lib/api";

const TIME_SLOTS = ["9:00 AM", "10:00 AM", "11:00 AM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM"];
const FACILITY_TYPES = ["Healthcare / Hospital", "Government / Federal", "Industrial / Manufacturing", "Commercial Office", "Education / K-12", "Distribution / Warehouse", "Municipal Facility", "Other"];

export default function WalkthroughScheduler({ variant = "light" }) {
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", facility_type: "", location: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!date || !time) return toast.error("Please pick a date and time.");
    if (!form.name || !form.email || !form.facility_type || !form.location) {
      return toast.error("Please complete name, email, facility type, and location.");
    }
    setBusy(true);
    try {
      await submitWalkthrough({
        ...form,
        preferred_date: date.toISOString().slice(0, 10),
        preferred_time: time,
      });
      setDone(true);
      toast.success("Walkthrough request received. We'll confirm within one business day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit request. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const dark = variant === "dark";
  const wrapCls = dark
    ? "bg-[var(--es-navy-deep)] text-white border border-white/10"
    : "bg-white border border-[var(--es-border)]";
  const labelCls = dark ? "text-white/70" : "text-[var(--es-text)]";
  const inputCls = dark
    ? "w-full bg-white/5 border border-white/10 text-white placeholder-white/40 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--es-accent)]"
    : "es-input";
  const slotBase = "text-xs md:text-sm border px-3 py-2.5 rounded-sm text-center font-semibold transition-colors";
  const slotIdle = dark
    ? "border-white/15 bg-white/5 text-white/85 hover:border-[var(--es-accent)]"
    : "border-[var(--es-border)] bg-white text-[var(--es-text)] hover:border-[var(--es-navy)]";
  const slotActive = "border-[var(--es-accent)] bg-[var(--es-accent)] text-[var(--es-navy)]";

  return (
    <div data-testid="walkthrough-scheduler" className={`${wrapCls} rounded-sm p-6 md:p-10`}>
      {done ? (
        <div data-testid="walkthrough-success-message" className="py-8 text-center">
          <CheckCircle size={56} weight="fill" className="mx-auto text-[var(--es-accent)]" />
          <h3 className={`mt-5 text-2xl font-bold ${dark ? "text-white" : "text-[var(--es-navy)]"}`} style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Walkthrough requested.</h3>
          <p className={`mt-3 ${dark ? "text-white/70" : "text-[var(--es-text-muted)]"} max-w-md mx-auto`}>
            We'll confirm your <strong>{date?.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</strong> visit at <strong>{time}</strong> within one business day.
          </p>
        </div>
      ) : (
        <form onSubmit={onSubmit} data-testid="walkthrough-form" className="grid gap-6">
          <div>
            <div className={`text-xs uppercase tracking-[0.25em] font-semibold ${dark ? "text-[var(--es-accent)]" : "text-[var(--es-accent)]"}`}>Book a Walkthrough</div>
            <h3 className={`mt-2 text-2xl md:text-3xl font-bold ${dark ? "text-white" : "text-[var(--es-navy)]"} leading-tight`} style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              Schedule an on-site walkthrough.
            </h3>
            <p className={`mt-3 text-sm ${dark ? "text-white/70" : "text-[var(--es-text-muted)]"} max-w-2xl`}>
              Pick a date and time. A member of our leadership team will meet you on-site, review your scope, and follow up with a tailored proposal.
            </p>
          </div>

          <div className="grid lg:grid-cols-12 gap-6">
            {/* Calendar */}
            <div className="lg:col-span-5">
              <div className={`text-[10px] uppercase tracking-widest font-semibold mb-2 ${labelCls}`}>Select a date (Mon–Fri)</div>
              <div className={`${dark ? "bg-white/5 border border-white/10" : "border border-[var(--es-border)] bg-white"} rounded-sm p-2 flex items-center justify-center`}>
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(d) => d < today || isWeekend(d)}
                  data-testid="walkthrough-calendar"
                  className={dark ? "text-white" : ""}
                />
              </div>
            </div>

            {/* Time + details */}
            <div className="lg:col-span-7 grid gap-5">
              <div>
                <div className={`text-[10px] uppercase tracking-widest font-semibold mb-2 ${labelCls}`}>Pick a time</div>
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                  {TIME_SLOTS.map((t) => (
                    <button type="button" key={t} data-testid={`walkthrough-time-${t.replace(/\W+/g, "-").toLowerCase()}`}
                      onClick={() => setTime(t)}
                      className={`${slotBase} ${time === t ? slotActive : slotIdle}`}>
                      <Clock size={12} weight="fill" className="inline mr-1" />{t}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <label className="block">
                  <span className={`text-[10px] uppercase tracking-widest font-semibold ${labelCls} block mb-1.5`}>Full Name *</span>
                  <input className={inputCls} required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="walkthrough-input-name" />
                </label>
                <label className="block">
                  <span className={`text-[10px] uppercase tracking-widest font-semibold ${labelCls} block mb-1.5`}>Email *</span>
                  <input type="email" className={inputCls} required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="walkthrough-input-email" />
                </label>
                <label className="block">
                  <span className={`text-[10px] uppercase tracking-widest font-semibold ${labelCls} block mb-1.5`}>Company / Agency</span>
                  <input className={inputCls} value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="walkthrough-input-company" />
                </label>
                <label className="block">
                  <span className={`text-[10px] uppercase tracking-widest font-semibold ${labelCls} block mb-1.5`}>Phone</span>
                  <input type="tel" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="walkthrough-input-phone" />
                </label>
                <label className="block md:col-span-1">
                  <span className={`text-[10px] uppercase tracking-widest font-semibold ${labelCls} block mb-1.5`}>Facility Type *</span>
                  <select className={inputCls} required value={form.facility_type} onChange={(e) => set("facility_type", e.target.value)} data-testid="walkthrough-input-facility-type">
                    <option value="">Select…</option>
                    {FACILITY_TYPES.map((t) => <option key={t} value={t} className="text-[var(--es-text)]">{t}</option>)}
                  </select>
                </label>
                <label className="block md:col-span-1">
                  <span className={`text-[10px] uppercase tracking-widest font-semibold ${labelCls} block mb-1.5`}><MapPin size={11} weight="fill" className="inline mr-1" />Site Address / City *</span>
                  <input className={inputCls} required placeholder="e.g. Asheboro, NC" value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="walkthrough-input-location" />
                </label>
              </div>

              <label className="block">
                <span className={`text-[10px] uppercase tracking-widest font-semibold ${labelCls} block mb-1.5`}>Notes (optional)</span>
                <textarea rows={3} className={inputCls} placeholder="Scope, escorts required, gate procedures…" value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="walkthrough-input-notes" />
              </label>

              <div className={`flex items-center justify-between gap-4 pt-2 border-t ${dark ? "border-white/10" : "border-[var(--es-border)]"}`}>
                <div className={`text-xs ${dark ? "text-white/60" : "text-[var(--es-text-muted)]"} flex items-center gap-2`}>
                  <CalendarBlank size={16} weight="fill" className="text-[var(--es-accent)]" />
                  {date ? date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) : "Pick a date"} · {time || "pick a time"}
                </div>
                <button type="submit" disabled={busy} data-testid="walkthrough-submit-button" className="es-btn-accent text-sm !py-2.5 disabled:opacity-50">
                  {busy ? "Booking…" : "Book walkthrough"} <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
